/* Streaming text chat client: POST /api/chat and read the SSE token stream. */

// The system prompt comes from the UI (avatar menu -> 自定义 System Prompt),
// passed in as `systemPrompt`; when empty the gateway applies its
// DEFAULT_INSTRUCTIONS.

/**
 * Stream a reply from the backend.
 *
 * @param {object} o
 * @param {string} o.prompt                 current user message
 * @param {Array<{role,text}>} o.history    prior turns
 * @param {string} [o.systemPrompt]         custom system prompt (empty -> gateway default)
 * @param {boolean} [o.thinking]            Thinking switch; omit for the model's default
 * @param {(acc:string)=>void} o.onToken    called with the accumulated text
 * @param {(acc:string,result:object)=>void} o.onDone called once with text and {status,error}
 * @param {()=>boolean} [o.shouldStop]       cooperative cancel
 */
export async function streamReply({ prompt, history = [], model, systemPrompt, thinking, onToken, onDone, shouldStop, signal }) {
  const messages = [
    // Only send a system turn when the UI supplies one; otherwise the gateway
    // applies its own DEFAULT_INSTRUCTIONS fallback.
    ...(systemPrompt ? [{ role: "system", content: systemPrompt }] : []),
    ...history.filter((m) => m.policyEligible !== false).map((m) => ({
      role: m.role === "user" ? "user" : "assistant",
      content: m.text,
    })),
    { role: "user", content: prompt },
  ];

  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  let acc = "";
  let result = { status: "failed", error: { code: "incomplete_stream", message: "回复连接提前结束，本轮未加入对话上下文，请重试。" } };
  let reader;
  try {
    const resp = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages, model, ...(typeof thinking === "boolean" ? { thinking } : {}),
        ...browserTimeZone() }),
      signal: controller.signal,
    });
    if (!resp.ok || !resp.body) {
      let body;
      try { body = await resp.json(); } catch {}
      const error = new Error(body?.error || body?.detail || `chat request failed: ${resp.status}`);
      error.code = body?.code || "http_error";
      error.details = body?.details;
      throw error;
    }

    reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    readFrames: while (true) {
      if (shouldStop && shouldStop()) { result = { status: "cancelled" }; controller.abort(); break; }
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") { result = { status: "completed" }; break readFrames; }
          let evt;
          try { evt = JSON.parse(data); }
          catch { throw new Error("模型返回的流格式无效，本轮未加入对话上下文，请重试。"); }
          if (evt.error) {
            result = { status: "failed", error: {
              message: typeof evt.error === "string" ? evt.error : evt.error.message || "本轮回复失败，请重试。",
              code: evt.code || evt.error.code || "upstream_error", details: evt.details || evt.error.details,
            } };
            break readFrames;
          }
          if (evt.delta) { acc += evt.delta; onToken(acc); }
        }
      }
    }
  } catch (err) {
    result = err.name === "AbortError" ? { status: "cancelled" } : {
      status: "failed", error: { message: err.message || "模型连接失败，请重试。", code: err.code || "connection_error", details: err.details },
    };
  } finally {
    signal?.removeEventListener("abort", abort);
    if (reader) { try { await reader.cancel(); } catch {} }
  }
  onDone(acc, result);
  return acc;
}

function browserTimeZone() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone ? { time_zone: timeZone } : {};
  } catch { return {}; }
}
